import { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.greidi.is';
const WS_BASE = API_BASE.replace(/^http/, 'ws');

/**
 * React hook for WebSocket-based real-time chat.
 * Manages connection lifecycle, auto-reconnect, heartbeat, and message routing.
 *
 * @param {string|null} jobId - Job ID to connect to (null = don't connect)
 * @param {string|null} token - JWT access token
 * @param {string|null} currentUserId - Current user's D1 ID
 * @param {Object} options
 * @param {Function} options.onNewMessage - Called when a new message arrives from WS
 * @param {Function} options.onReadReceipt - Called when a read receipt arrives
 */
export function useWebSocket(jobId, token, currentUserId, { onNewMessage, onReadReceipt } = {}) {
  const [connected, setConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // { userId: { name, timeout } }
  const [onlineCount, setOnlineCount] = useState(0);

  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const mountedRef = useRef(true);

  // Stable callback refs
  const onNewMessageRef = useRef(onNewMessage);
  const onReadReceiptRef = useRef(onReadReceipt);
  useEffect(() => { onNewMessageRef.current = onNewMessage; }, [onNewMessage]);
  useEffect(() => { onReadReceiptRef.current = onReadReceipt; }, [onReadReceipt]);

  const cleanup = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Cleanup');
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!jobId || !token || !currentUserId) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    cleanup();

    const url = `${WS_BASE}/ws/chat/${jobId}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      setConnected(true);
      reconnectAttemptRef.current = 0;

      // Start heartbeat
      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onclose = (event) => {
      if (!mountedRef.current) return;
      setConnected(false);

      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }

      // Auto-reconnect with exponential backoff (unless intentional close)
      if (event.code !== 1000) {
        const attempt = reconnectAttemptRef.current;
        const delay = Math.min(3000 * Math.pow(2, attempt), 30000);
        reconnectAttemptRef.current = attempt + 1;

        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current) connect();
        }, delay);
      }
    };

    ws.onerror = () => {
      // onclose will fire after onerror, so we handle reconnection there
      console.warn('[WS] Connection error');
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;

      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      switch (data.type) {
        case 'message':
          if (data.message && onNewMessageRef.current) {
            onNewMessageRef.current(data.message);
          }
          break;

        case 'typing': {
          const uid = data.user_id;
          if (uid === currentUserId) break; // Ignore own typing
          setTypingUsers(prev => {
            const next = { ...prev };
            // Clear existing timeout
            if (next[uid]?.timeout) clearTimeout(next[uid].timeout);
            // Set timeout to remove after 3 seconds
            const timeout = setTimeout(() => {
              if (!mountedRef.current) return;
              setTypingUsers(p => {
                const updated = { ...p };
                delete updated[uid];
                return updated;
              });
            }, 3000);
            next[uid] = { name: data.user_name || 'Einhver', timeout };
            return next;
          });
          break;
        }

        case 'presence':
          if (typeof data.online_count === 'number') {
            setOnlineCount(data.online_count);
          }
          break;

        case 'read':
          if (data.message_id && onReadReceiptRef.current) {
            onReadReceiptRef.current(data.message_id, data.user_id);
          }
          break;

        case 'pong':
          // Heartbeat acknowledged
          break;

        case 'error':
          console.warn('[WS] Server error:', data.error);
          break;
      }
    };
  }, [jobId, token, currentUserId, cleanup]);

  // Connect when jobId/token change
  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [connect, cleanup]);

  // Send a chat message
  const sendMessage = useCallback((text, toUserId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', text, to_user_id: toUserId }));
      return true;
    }
    return false; // Caller should fall back to REST
  }, []);

  // Send typing indicator (debounced to max once per 2s)
  const sendTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  }, []);

  // Mark a message as read
  const markRead = useCallback((messageId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'read', message_id: messageId }));
    }
  }, []);

  return {
    connected,
    typingUsers,
    onlineCount,
    sendMessage,
    sendTyping,
    markRead,
  };
}
